import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";

const Confidentialite = () => {
  const { t, locale } = useLanguage("legal");
  useSEO({ title: t("confidentialite.title"), canonical: "/politique-confidentialite", locale });
  const items = (key: string): string[] => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };
  const objects = (key: string): Array<Record<string, string>> => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };
  return (
    <div className="min-h-screen" dir={locale === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main className="container max-w-3xl py-10 space-y-10">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <h1 className="font-display text-3xl font-bold">{t("confidentialite.title")}</h1>
          </div>
          <p className="text-sm text-muted-foreground">{t("confidentialite.last_updated")}</p>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.intro")}</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s1_title")}</h2>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="font-medium">{t("confidentialite.s1_platform")}</span><span>Hirafi</span>
            <span className="font-medium">{t("confidentialite.s1_email")}</span><span>contact@hirafidz.com</span>
            <span className="font-medium">{t("confidentialite.s1_siege")}</span><span>{t("confidentialite.s1_siege_val")}</span>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s2_title")}</h2>
          <p className="text-muted-foreground">{t("confidentialite.s2_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("confidentialite.s2_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s3_title")}</h2>
          <p className="text-muted-foreground">{t("confidentialite.s3_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("confidentialite.s3_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s4_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.s4_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s5_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.s5_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s6_title")}</h2>
          <p className="text-muted-foreground">{t("confidentialite.s6_intro")}</p>
          <ul className="space-y-2 text-muted-foreground">
            {objects("confidentialite.s6_items").map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-medium text-foreground min-w-40">{item.label}</span>
                <span>{item.desc}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm">{t("confidentialite.s6_contact")} <a href="mailto:contact@hirafidz.com" className="text-primary hover:underline">contact@hirafidz.com</a></p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s7_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.s7_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s8_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.s8_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s9_title")}</h2>
          <p className="text-muted-foreground">{t("confidentialite.s9_intro")}</p>
          <div className="space-y-2">
            {objects("confidentialite.s9_items").map((item, i) => (
              <div key={i} className="rounded border p-3 text-sm">
                <p className="font-semibold">{item.name}</p>
                <p className="text-muted-foreground">{item.role}</p>
                <p className="text-muted-foreground text-xs">{item.addr}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s10_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("confidentialite.s10_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("confidentialite.s11_title")}</h2>
          <p className="text-muted-foreground">{t("confidentialite.s11_body")}</p>
          <a href="mailto:contact@hirafidz.com" className="text-primary hover:underline">contact@hirafidz.com</a>
        </section>

        <div className="border-t pt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/cgu" className="hover:text-primary hover:underline">{t("confidentialite.link_cgu")}</Link>
          <Link to="/mentions-legales" className="hover:text-primary hover:underline">{t("confidentialite.link_mentions")}</Link>
          <Link to="/a-propos" className="hover:text-primary hover:underline">{t("confidentialite.link_apropos")}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Confidentialite;
